import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SavingOverlay from "../SavingOverlay";
import { Toast } from "../Toast";
import { useToast } from "../../hooks/useToast";
import { colors } from "../../theme/designSystem";
import {
  professionalServiceValueService,
  ProfessionalServiceValueItem,
} from "../../services/professionalServiceValueService";

interface Option {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  professionalRegistration: string;
  professionalName: string;
  especialidade: string;
  teams: Option[];
  services: Option[];
  value: ProfessionalServiceValueItem | null; // null = criar
  readOnly?: boolean; // visualização (registro com agendamento vinculado)
  onSaved: () => void;
}

interface FormState {
  professionalTeam: string;
  serviceType: string;
  sessionTimeMinutes: string;
  professionalPaymentInc: boolean;
  serviceValue: string;
  professionalPaymentVal: string;
}

const emptyForm: FormState = {
  professionalTeam: "",
  serviceType: "",
  sessionTimeMinutes: "0",
  professionalPaymentInc: false,
  serviceValue: "0,00",
  professionalPaymentVal: "0,00",
};

// Máscara de moeda baseada em centavos: dígitos da direita para a esquerda -> "1.234,56".
const maskMoney = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  const n = parseInt(digits || "0", 10);
  return (n / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMoney = (n: number): string =>
  n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseMoney = (v: string): number => {
  const n = parseFloat(v.replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const ProfessionalServiceValueModal: React.FC<Props> = ({
  isOpen,
  onClose,
  professionalRegistration,
  professionalName,
  especialidade,
  teams,
  services,
  value,
  readOnly = false,
  onSaved,
}) => {
  const { toast, showToast, hideToast } = useToast();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<
    { id: string; professionalTeam?: string; serviceType?: string }[]
  >([]);
  const isEdit = !!value;

  // Carrega os valores já cadastrados do profissional para não oferecer serviço duplicado
  // (mesmo profissional + equipe + serviço).
  useEffect(() => {
    if (!isOpen || !professionalRegistration) return;
    professionalServiceValueService
      .getByProfessional({
        professionalRegistration,
        active: true,
        pageNumber: 1,
        pageSize: 500,
      })
      .then((r) =>
        setExisting(
          r.items.map((i) => ({
            id: i.id,
            professionalTeam: i.professionalTeam,
            serviceType: i.serviceType,
          }))
        )
      )
      .catch(() => setExisting([]));
  }, [isOpen, professionalRegistration]);

  // Serviços disponíveis: remove os já cadastrados para a equipe selecionada
  // (na edição, mantém o próprio registro).
  const availableServices = services.filter((s) => {
    const takenByOther = existing.some(
      (e) =>
        e.serviceType === s.id &&
        (e.professionalTeam || "") === (form.professionalTeam || "") &&
        e.id !== value?.id
    );
    return !takenByOther;
  });

  useEffect(() => {
    if (!isOpen) return;
    setSaving(false);
    if (value) {
      setForm({
        professionalTeam: value.professionalTeam || "",
        serviceType: value.serviceType || "",
        sessionTimeMinutes:
          value.sessionTimeMinutes != null
            ? String(value.sessionTimeMinutes)
            : "0",
        professionalPaymentInc: !!value.professionalPaymentInc,
        serviceValue:
          value.serviceValue != null ? formatMoney(value.serviceValue) : "0,00",
        professionalPaymentVal:
          value.professionalPaymentVal != null
            ? formatMoney(value.professionalPaymentVal)
            : "0,00",
      });
    } else {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value]);

  const teamMissing = form.professionalTeam.length === 0;
  const serviceMissing = form.serviceType.length === 0;

  const handleSave = async () => {
    if (saving) return;
    if (teamMissing) {
      showToast("Selecione a equipe.", "error");
      return;
    }
    if (serviceMissing) {
      showToast("Selecione o serviço.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        professionalTeam: form.professionalTeam || undefined,
        serviceType: form.serviceType,
        serviceValue: parseMoney(form.serviceValue),
        professionalPaymentVal: parseMoney(form.professionalPaymentVal),
        sessionTimeMinutes: Number(form.sessionTimeMinutes) || 0,
        professionalPaymentInc: form.professionalPaymentInc,
      };

      if (isEdit && value) {
        await professionalServiceValueService.update(value.id, payload);
      } else {
        await professionalServiceValueService.create(
          professionalRegistration,
          payload
        );
      }

      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao salvar. Tente novamente.";
      showToast(message, "error");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", position: "relative", overflow: "hidden" },
      }}
    >
      <SavingOverlay open={saving} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
        }}
      >
        {readOnly
          ? "Visualizar serviço (agendamento vinculado)"
          : isEdit
            ? "Editar valor de serviço"
            : "Novo valor de serviço"}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ color: "white" }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
      >
        <TextField
          label="Profissional"
          value={professionalName}
          fullWidth
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Especialidade"
          value={especialidade}
          fullWidth
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Equipe"
          value={form.professionalTeam}
          onChange={(e) =>
            // Trocar a equipe limpa o serviço (o disponível depende da equipe).
            setForm((f) => ({
              ...f,
              professionalTeam: e.target.value,
              serviceType: "",
            }))
          }
          required
          disabled={readOnly}
          error={teamMissing}
          fullWidth
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Selecione</MenuItem>
          {teams.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Serviço"
          value={form.serviceType}
          onChange={(e) =>
            setForm((f) => ({ ...f, serviceType: e.target.value }))
          }
          required
          disabled={readOnly || teamMissing}
          error={!teamMissing && serviceMissing}
          helperText={teamMissing ? "Selecione a equipe primeiro." : undefined}
          fullWidth
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">Selecione</MenuItem>
          {availableServices.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Tempo padrão de sessão (minutos)"
          type="number"
          value={form.sessionTimeMinutes}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              sessionTimeMinutes: e.target.value.replace(/\D/g, ""),
            }))
          }
          inputProps={{ inputMode: "numeric", min: 0, maxLength: 4 }}
          disabled={readOnly}
          sx={{ width: { xs: "100%", sm: 220 } }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.professionalPaymentInc}
              disabled={readOnly}
              disableRipple
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  professionalPaymentInc: e.target.checked,
                }))
              }
              sx={{
                color: colors.primary,
                "&.Mui-checked": { color: colors.primary },
                "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                "&.Mui-checked.Mui-disabled": { color: "rgba(0, 0, 0, 0.38)" },
              }}
            />
          }
          label="Recebe independente da realização do serviço agendado (não cancelado pelo profissional)"
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Valor Total (R$)"
            value={form.serviceValue}
            onChange={(e) =>
              setForm((f) => ({ ...f, serviceValue: maskMoney(e.target.value) }))
            }
            disabled={readOnly}
            inputProps={{ inputMode: "numeric", maxLength: 14 }}
            sx={{ flex: 1, minWidth: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Valor do profissional (R$)"
            value={form.professionalPaymentVal}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                professionalPaymentVal: maskMoney(e.target.value),
              }))
            }
            disabled={readOnly}
            inputProps={{ inputMode: "numeric", maxLength: 14 }}
            sx={{ flex: 1, minWidth: 160 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "1.5rem 2rem",
          borderTop: `1px solid ${colors.backgroundAlt}`,
          backgroundColor: colors.background,
          gap: "1rem",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || readOnly}
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryHover },
          }}
        >
          Salvar
        </Button>
      </DialogActions>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Dialog>
  );
};

export default ProfessionalServiceValueModal;
